import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import {
  Box,
  CardActions,
  CardContent,
  Grid,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContactInfoWithPrincipalIcon } from '../../../clients.model'
import { styles } from './commercialsCard.styles'

import ButtonWithPermissions from '../../../../../components/buttonWithPermissions/buttonWithPermissions'
import { CardWithTransition } from '../../../../../components/shared/cardWithTransition/CardWithTransition'
import { LoadingCard } from '../../../../../components/shared/loadingCard/loadingCard'
import { formatPhoneNumberHelper } from '../../../../../helpers/formatPhoneHelper'
import { getUserPermissions, hasPermission } from '../../../../../helpers/permissionsHelper'
import { Pages, Resources } from '../../../../../model/RBAC/constants'
import { EmptyContent } from '../shared/emptyCard'
import { Header } from '../shared/header'
import { CommercialModel } from '../../../../../api/Argentia/model/commercials/commercial'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHandshake, faLayerGroup, faArrowsAlt } from '@fortawesome/free-solid-svg-icons'
import { CommercialTableModel } from './commercialsCard.types'
import { Delete, Edit } from '@material-ui/icons'
import { NonAuthRoutes } from '../../../../../model'
import { useHistory } from 'react-router'
import { deleteCommercial } from '../../../../commercial/commercials/commercials.service'
import { fetchClientCommercialsByID } from '../../../../../redux'
import { useSelector } from 'react-redux'
import { selectClientData } from '../../../../../redux/selectors/client.selectors'
import { useConfirmationDialog } from '../../../../../hooks'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { EnumCommercialRelationActionType } from '../../../../../api/oData/enums/enumCommercialRelationActionType'
import i18n from '../../../../../i18n'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export interface CommercialsCardProps {
  isFetchingClient: boolean
  isFetchingCommercials: boolean
  commercials?: CommercialTableModel[]
  error: boolean
  onRetry: () => void
}

export const CommercialsCard: React.FC<CommercialsCardProps> = ({
  isFetchingClient,
  isFetchingCommercials,
  commercials,
  error,
  onRetry
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'clientFile', 'enums'])
  const history = useHistory()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const clientData = useSelector(selectClientData)

  const [existsSubjectLevelCommercial, setExistsSubjectLevelCommercial] = useState<boolean>(false)

  const validateCommercials = (commercials: CommercialTableModel[]) => {
    if (commercials.some(c => c.relationLevel === EnumCommercialRelationActionType.Subject)) {
      setExistsSubjectLevelCommercial(true)
    } else {
      setExistsSubjectLevelCommercial(false)
    }
  }

  useEffect(() => {
    commercials && validateCommercials(commercials)
  }, [commercials])

  if (isFetchingCommercials) {
    return <LoadingCard />
  }

  const CommercialsHeader = () =>
    React.useMemo(() => {
      return (
        <Header
          avatarIcon={<FontAwesomeIcon size={'lg'} icon={faHandshake as IconProp} />}
          title={t('clientFile:components.commercialsCard.title')}
          titleVariant="secondary"
        />
      )
    }, [t])

  const handleOnAddCommercial = () => {
    clientData?.id &&
      history.push(NonAuthRoutes.CommercialsDetails, {
        clientID: clientData.id
      })
  }

  const handleEditCommercial = (id: number): void => {
    history.push(NonAuthRoutes.CommercialsDetails, { commercialID: id })
  }

  const handleDeleteCommercial = async (id: number) => {
    await getConfirmation(ConfirmationDialogs.DELETE_COMMERCIAL).then(async confirm => {
      await deleteCommercial(id).then(response => {
        if (response.success && clientData?.id) {
          info(
            i18n.t('clientFile:components.commercialsCard.dataTable.notifications.success.delete')
          )
          fetchClientCommercialsByID(clientData.id)
        }
      })
    })
    closeConfirmation()
  }

  if (error) {
    return (
      <CardWithTransition show={true} raised>
        <CommercialsHeader />
        <EmptyContent title={t('clientFile:components.commercialsCard.errorCard.errorMessage')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={onRetry}
          >
            {t('clientFile:components.commercialsCard.errorCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  if (!commercials || commercials.length === 0) {
    return (
      <CardWithTransition show={true} raised>
        <CommercialsHeader />
        <EmptyContent title={t('clientFile:components.commercialsCard.emptyCard.title')}>
          <ButtonWithPermissions
            hasPermission={true}
            size="small"
            color="primary"
            variant="contained"
            onClick={handleOnAddCommercial}
          >
            {t('clientFile:components.commercialsCard.emptyCard.buttonLabel')}
          </ButtonWithPermissions>
        </EmptyContent>
      </CardWithTransition>
    )
  }

  if (commercials.length > 1) {
    const columns: DataTableColumn[] = [
      {
        name: 'commercialSubjectDesc',
        label: 'Comercial',
        type: DataType.LONGTEXT,
        columnWidth: 180
      },
      {
        name: 'productDesc',
        // label: t('clientFile:components.CommercialsCard.dataTable.columns.contactName'),
        label: 'Producto',
        type: DataType.LONGTEXT
        // columnWidth: 4
      },
      {
        name: 'relatedProductDesc',
        // label: t('clientFile:components.CommercialsCard.dataTable.columns.contactName'),
        label: 'Asociado a',
        type: DataType.LONGTEXT,
        columnWidth: 200
      }
    ]

    const tableOptions: DataTableOptions = {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      elevation: 0,
      download: false,
      print: false,
      selectableRows: false,
      canEdit: false,
      canDelete: false,
      canInsert: false,
      pagination: true,
      rowsPerPage: 5,
      rowsPerPageOptions: [5, 10, 20],
      rowCustomActions: [
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('clientFile:components.commercialsCard.dataTable.customActions.edit'),
          onClick: (row: CommercialTableModel) => handleEditCommercial(row.id),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('clientFile:components.commercialsCard.dataTable.customActions.delete'),
          onClick: (row: CommercialTableModel) => handleDeleteCommercial(row.id),
          userPermission: UserPermission.EDIT
        }
      ]
    }

    // const tableData = mapCommercials(commercials)

    return (
      // <>{console.log('commercials', commercials)}</>
      <CardWithTransition show={true} raised>
        <CommercialsHeader />
        <Box m={2} mt={0}>
          <DataTable
            className={classes.dataTableWithoutHeader}
            data={commercials}
            columns={columns}
            options={tableOptions}
            userPermissions={getUserPermissions(Pages.ClientFile)}
          />
        </Box>
        <CardActions className={classes.cardActionsRight}>
          <ButtonWithPermissions
            size="small"
            color="primary"
            disabled={existsSubjectLevelCommercial}
            hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
            onClick={handleOnAddCommercial}
          >
            {t('clientFile:components.commercialsCard.actions.add')}
          </ButtonWithPermissions>
        </CardActions>
      </CardWithTransition>
    )
  }

  const { commercialSubjectDesc, productDesc, relatedProductDesc } = commercials[0]

  return (
    <CardWithTransition show={true} raised>
      <CommercialsHeader />
      <CardContent className={classes.cardContent}>
        {/* {commercials ? JSON.stringify(commercials) : 'empty'} */}
        <Grid container spacing={1} direction="column">
          <Grid item className={classes.commercialName}>
            {commercialSubjectDesc ? (
              <Typography variant="h2" color="secondary">
                {commercialSubjectDesc}
              </Typography>
            ) : (
              ''
            )}
          </Grid>
          {productDesc ? (
            <Grid container item spacing={2} direction="row">
              <Grid item>
                <FontAwesomeIcon
                  color={currentTheme.palette.primary.main}
                  icon={faLayerGroup as IconProp}
                />
              </Grid>
              <Grid item>
                <Typography variant="body2" className={classes.description}>
                  {productDesc}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
          {relatedProductDesc ? (
            <Grid container item spacing={2} direction="row">
              <Grid item>
                <FontAwesomeIcon
                  color={currentTheme.palette.primary.main}
                  icon={faArrowsAlt as IconProp}
                />
              </Grid>
              <Grid item>
                <Typography variant="body2" className={classes.description}>
                  {relatedProductDesc}
                </Typography>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
      <CardActions className={classes.cardActionsRight}>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={() => handleDeleteCommercial(commercials[0].id)}
        >
          {t('clientFile:components.commercialsCard.actions.delete')}
        </ButtonWithPermissions>
        <ButtonWithPermissions
          size="small"
          color="primary"
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={() => handleEditCommercial(commercials[0].id)}
        >
          {t('clientFile:components.commercialsCard.actions.edit')}
        </ButtonWithPermissions>
        <ButtonWithPermissions
          size="small"
          color="primary"
          disabled={existsSubjectLevelCommercial}
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={handleOnAddCommercial}
        >
          {t('clientFile:components.commercialsCard.actions.add')}
        </ButtonWithPermissions>
      </CardActions>
    </CardWithTransition>
  )
}
